import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { PhotoGeoJSON, PhotoFeatureProperties } from '../../services/mapGeoJson';
import { PhotoPopup } from './PhotoPopup';

export interface TravelMapProps {
  photosGeoJson: PhotoGeoJSON;
  routeGeoJson?: GeoJSON.FeatureCollection<GeoJSON.LineString | GeoJSON.MultiLineString>;
  selectedPhotoId?: string | null;
  onPhotoSelect?: (photoId: string) => void;
  onPhotoExpand?: (photoId: string) => void;
  basemapStyle?: string;
  className?: string;
}

export const OPENFREEMAP_POSITRON = 'https://tiles.openfreemap.org/styles/positron';
export const OPENFREEMAP_LIBERTY = 'https://tiles.openfreemap.org/styles/liberty';

export const TravelMap: React.FC<TravelMapProps> = ({
  photosGeoJson,
  routeGeoJson,
  selectedPhotoId,
  onPhotoSelect,
  onPhotoExpand,
  basemapStyle = OPENFREEMAP_POSITRON,
  className = '',
}) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const activePopupRef = useRef<maplibregl.Popup | null>(null);
  const popupNodeRef = useRef<HTMLDivElement | null>(null);

  const [activePhoto, setActivePhoto] = useState<PhotoFeatureProperties | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState<boolean>(false);
  const hasAutoFitRef = useRef<boolean>(false);

  // Stable refs for callbacks to prevent WebGL context churn
  const onPhotoSelectRef = useRef(onPhotoSelect);
  onPhotoSelectRef.current = onPhotoSelect;
  const onPhotoExpandRef = useRef(onPhotoExpand);
  onPhotoExpandRef.current = onPhotoExpand;

  // Close active popup helper
  const closeActivePopup = useCallback(() => {
    if (activePopupRef.current) {
      activePopupRef.current.remove();
      activePopupRef.current = null;
    }
    setActivePhoto(null);
  }, []);

  const closeActivePopupRef = useRef(closeActivePopup);
  closeActivePopupRef.current = closeActivePopup;

  // Handle Level 2 Fullscreen Lightbox trigger
  const handleExpandPhoto = useCallback((photoId: string) => {
    closeActivePopup();
    if (onPhotoExpandRef.current) {
      onPhotoExpandRef.current(photoId);
    }
  }, [closeActivePopup]);

  // --------------------------------------------------------------------------
  // Map Initialization & Teardown
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Create container for React Portal popup
    const popupEl = document.createElement('div');
    popupNodeRef.current = popupEl;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: basemapStyle,
      center: [15, 45],
      zoom: 3,
      attributionControl: false,
      touchZoomRotate: true,
      touchPitch: true,
      dragRotate: true,
      trackResize: true,
    });

    mapRef.current = map;

    // Mobile-optimized navigation controls
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');
    map.addControl(
      new maplibregl.NavigationControl({
        showCompass: true,
        showZoom: true,
        visualizePitch: true,
      }),
      'bottom-right'
    );

    // Track readiness flag for headless captures
    map.once('idle', () => {
      if (typeof window !== 'undefined') {
        (window as any).__MAP_READY__ = true;
        (window as any).__MAP__ = map;
      }
    });

    map.on('dataloading', () => {
      if (typeof window !== 'undefined') {
        (window as any).__MAP_READY__ = false;
      }
    });

    map.on('idle', () => {
      if (typeof window !== 'undefined') {
        (window as any).__MAP_READY__ = true;
        (window as any).__MAP__ = map;
      }
    });

    // Prevent tab crashes on background tab WebGL context loss
    map.on('webglcontextlost', (e: any) => {
      e?.preventDefault?.();
      console.warn('MapLibre WebGL context lost; awaiting automatic restore.');
    });

    map.on('load', () => {
      // 1. Route Track Source & Layers (LineString)
      map.addSource('route', {
        type: 'geojson',
        data: routeGeoJson || {
          type: 'FeatureCollection',
          features: [],
        },
      });

      // Route Casing (dark outline)
      map.addLayer({
        id: 'route-casing',
        type: 'line',
        source: 'route',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#0f172a',
          'line-width': 6,
          'line-opacity': 0.75,
        },
      });

      // Route Primary Track (Sky blue dashed)
      map.addLayer({
        id: 'route-line',
        type: 'line',
        source: 'route',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#0ea5e9',
          'line-width': 3.5,
          'line-dasharray': [2, 1],
        },
      });

      // 2. Selected Photo Halo Source & Layer
      map.addSource('selected-photo', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });

      map.addLayer({
        id: 'selected-photo-halo',
        type: 'circle',
        source: 'selected-photo',
        paint: {
          'circle-radius': 19,
          'circle-color': '#38bdf8',
          'circle-opacity': 0.35,
          'circle-stroke-width': 2.5,
          'circle-stroke-color': '#ffffff',
        },
      });

      // 3. Photos GeoJSON Source with Supercluster
      console.log('Adding photos source with feature count:', photosGeoJson?.features?.length);
      try {
        map.addSource('photos', {
          type: 'geojson',
          data: photosGeoJson,
          cluster: true,
          clusterRadius: 50,
          clusterMaxZoom: 14,
        });
      } catch (err) {
        console.error('Failed to add photos source:', err);
      }

      // 4. Clusters Circle Layer (Stepped Sizing & Palette)
      try {
        map.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'photos',
          filter: ['has', 'point_count'],
          paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#0d9488', 10,  // < 10 photos: Teal
            '#f59e0b', 50,  // 10 - 49 photos: Amber
            '#ef4444', 250, // 50 - 249 photos: Coral Red
            '#8b5cf6'       // 250+ photos: Violet
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            18, 10,
            23, 50,
            28, 250,
            34
          ],
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.92,
            'circle-stroke-opacity': 0.95,
          },
        });
      } catch (err) {
        console.error('Failed to add clusters layer:', err);
      }

      // 5. Cluster Count Typography Layer
      try {
        map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'photos',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Noto Sans Regular'],
            'text-size': [
              'step',
              ['get', 'point_count'],
              12, 50,
              13, 250,
              14
            ],
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          },
          paint: {
            'text-color': '#ffffff',
          },
        });
      } catch (err) {
        console.error('Failed to add cluster-count layer:', err);
      }

      // 6. Unclustered Photo Points (Category Accent Circles)
      try {
        map.addLayer({
          id: 'unclustered-photo-points',
          type: 'circle',
          source: 'photos',
          filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'match',
            ['get', 'category'],
            ['Con me', 'Foto con me'], '#f43f5e',
            ['Panorami', 'Paesaggi', 'Landscape'], '#0ea5e9',
            ['Dettagli', 'Details'], '#10b981',
            ['Cibo', 'Food'], '#f59e0b',
            ['Architettura', 'Architecture'], '#6366f1',
            '#3b82f6'
          ],
          'circle-radius': 8.5,
          'circle-stroke-width': 2.5,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.95,
        },
      });

      // 7. Inner Lens Dot for Unclustered Photos
      map.addLayer({
        id: 'unclustered-photo-inner',
        type: 'circle',
        source: 'photos',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#ffffff',
          'circle-radius': 3,
          'circle-opacity': 0.9,
        },
      });
    } catch (err) {
      console.error('Failed to add unclustered photo layers:', err);
    }

      // Cursor changes on hover
      const setPointer = () => {
        if (map.getCanvas()) map.getCanvas().style.cursor = 'pointer';
      };
      const resetCursor = () => {
        if (map.getCanvas()) map.getCanvas().style.cursor = '';
      };

      map.on('mouseenter', 'clusters', setPointer);
      map.on('mouseleave', 'clusters', resetCursor);
      map.on('mouseenter', 'unclustered-photo-points', setPointer);
      map.on('mouseleave', 'unclustered-photo-points', resetCursor);

      setIsMapLoaded(true);
    });

    // ------------------------------------------------------------------------
    // Cluster Click-to-Zoom Expansion
    // ------------------------------------------------------------------------
    map.on('click', 'clusters', async (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
      if (!features || features.length === 0) return;

      const clusterFeature = features[0];
      const clusterId = clusterFeature.properties?.cluster_id;
      if (clusterId === undefined || clusterId === null) return;

      const source = map.getSource('photos') as maplibregl.GeoJSONSource;
      if (!source) return;

      try {
        const expansionZoom = await source.getClusterExpansionZoom(clusterId);
        const coordinates = (clusterFeature.geometry as GeoJSON.Point).coordinates as [number, number];

        map.easeTo({
          center: coordinates,
          zoom: Math.min(expansionZoom, map.getMaxZoom()),
          duration: 500,
          essential: true,
        });
      } catch (err) {
        console.warn('Failed to expand cluster zoom:', err);
      }
    });

    // ------------------------------------------------------------------------
    // Unclustered Photo Marker Click -> Single HTML Popup
    // ------------------------------------------------------------------------
    map.on('click', 'unclustered-photo-points', (e) => {
      const features = map.queryRenderedFeatures(e.point, { layers: ['unclustered-photo-points'] });
      if (!features || features.length === 0) return;

      const feature = features[0];
      const props = feature.properties as PhotoFeatureProperties;
      const coordinates = (feature.geometry as GeoJSON.Point).coordinates.slice() as [number, number];

      // Close prior popup
      if (activePopupRef.current) {
        activePopupRef.current.remove();
        activePopupRef.current = null;
      }

      if (popupNodeRef.current) {
        const popup = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 14,
          maxWidth: '320px',
          className: 'travel-photo-popup',
        })
          .setLngLat(coordinates)
          .setDOMContent(popupNodeRef.current)
          .addTo(map);

        activePopupRef.current = popup;
        setActivePhoto(props);
      }

      if (onPhotoSelectRef.current && props.id) {
        onPhotoSelectRef.current(props.id);
      }
    });

    // Dismiss popup on tap outside
    map.on('click', (e) => {
      const clusterFeatures = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
      const photoFeatures = map.queryRenderedFeatures(e.point, { layers: ['unclustered-photo-points'] });
      if (clusterFeatures.length === 0 && photoFeatures.length === 0) {
        if (closeActivePopupRef.current) {
          closeActivePopupRef.current();
        }
      }
    });

    // ------------------------------------------------------------------------
    // Container ResizeObserver (Eliminates Flexbox Zero-Height & Aspect Ratio Bugs)
    // ------------------------------------------------------------------------
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        if (mapRef.current && mapRef.current.getCanvas()) {
          mapRef.current.resize();
        }
      });
    });
    resizeObserver.observe(mapContainerRef.current);

    // Initial sizing sync
    requestAnimationFrame(() => {
      map.resize();
    });

    // Cleanup on unmount
    return () => {
      resizeObserver.disconnect();
      if (activePopupRef.current) {
        activePopupRef.current.remove();
        activePopupRef.current = null;
      }
      map.remove();
      mapRef.current = null;
      setIsMapLoaded(false);
      if (typeof window !== 'undefined') {
        delete (window as any).__MAP_READY__;
      }
    };
  }, [basemapStyle]);

  // --------------------------------------------------------------------------
  // Update Photos GeoJSON dynamically (Filter Pills / Timeline Slider)
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!isMapLoaded || !mapRef.current) return;
    const source = mapRef.current.getSource('photos') as maplibregl.GeoJSONSource;
    if (source) {
      source.setData(photosGeoJson);
    }
  }, [photosGeoJson, isMapLoaded]);

  // --------------------------------------------------------------------------
  // Update Route GeoJSON dynamically
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!isMapLoaded || !mapRef.current || !routeGeoJson) return;
    const source = mapRef.current.getSource('route') as maplibregl.GeoJSONSource;
    if (source) {
      source.setData(routeGeoJson);
    }
  }, [routeGeoJson, isMapLoaded]);

  // --------------------------------------------------------------------------
  // Synchronize Selected Photo Halo & Smooth Map Centering
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!isMapLoaded || !mapRef.current) return;

    const haloSource = mapRef.current.getSource('selected-photo') as maplibregl.GeoJSONSource;
    if (!haloSource) return;

    if (!selectedPhotoId) {
      haloSource.setData({ type: 'FeatureCollection', features: [] });
      return;
    }

    const feature = photosGeoJson.features.find((f) => f.properties.id === selectedPhotoId);
    if (feature && feature.geometry.type === 'Point') {
      haloSource.setData({
        type: 'FeatureCollection',
        features: [feature],
      });

      const coords = feature.geometry.coordinates as [number, number];
      mapRef.current.easeTo({
        center: coords,
        duration: 400,
      });

      // Synchronize popup display on selected photo
      if (popupNodeRef.current) {
        if (activePopupRef.current) {
          activePopupRef.current.remove();
          activePopupRef.current = null;
        }
        const popup = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: 14,
          maxWidth: '320px',
          className: 'travel-photo-popup',
        })
          .setLngLat(coords)
          .setDOMContent(popupNodeRef.current)
          .addTo(mapRef.current);

        activePopupRef.current = popup;
        setActivePhoto(feature.properties);
      }
    }
  }, [selectedPhotoId, photosGeoJson, isMapLoaded]);

  // --------------------------------------------------------------------------
  // Auto-fit Bounds on First Load or Trip Change
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (photosGeoJson.features.length === 0) {
      hasAutoFitRef.current = false;
      return;
    }

    if (!isMapLoaded || !mapRef.current || hasAutoFitRef.current) return;

    const bounds = new maplibregl.LngLatBounds();
    let count = 0;

    for (const f of photosGeoJson.features) {
      if (f.geometry && f.geometry.type === 'Point') {
        const [lon, lat] = f.geometry.coordinates;
        if (Number.isFinite(lon) && Number.isFinite(lat)) {
          bounds.extend([lon, lat]);
          count++;
        }
      }
    }

    if (count > 0) {
      hasAutoFitRef.current = true;
      mapRef.current.fitBounds(bounds, {
        padding: { top: 60, bottom: 60, left: 40, right: 40 },
        maxZoom: 15,
        duration: 800,
      });
    }
  }, [isMapLoaded, photosGeoJson]);

  return (
    <div className={`relative w-full h-full overflow-hidden select-none ${className}`}>
      {/* MapLibre Canvas Container */}
      <div ref={mapContainerRef} className="absolute inset-0 w-full h-full" />

      {/* React Portal for Single-Element HTML Popup */}
      {activePhoto &&
        popupNodeRef.current &&
        createPortal(
          <PhotoPopup
            photo={activePhoto}
            onExpand={handleExpandPhoto}
            onClose={closeActivePopup}
          />,
          popupNodeRef.current
        )}
    </div>
  );
};

export default TravelMap;
